import { useCallback, useState } from 'react';
import Molten from '@leverege/molten';
import { Toast } from '@leverege/ui-elements';
import { GlobalState } from '@leverege/ui-redux';
import showResponseToasts from './showResponseToasts';
const useUpdateDeviceAndStateConfigs = ({
  placeHolder,
  sku,
  locations,
  selectedDeviceConfiguration,
  selectedStateConfiguration,
  deviceConfigurationOptions,
  stateConfigurationOptions,
  onClose,
  server
}) => {
  const [loading, setLoading] = useState(false);
  const isConfigValid = selected => {
    const isConfigPlaceHolderSelected = selected === placeHolder.id;
    return !isConfigPlaceHolderSelected;
  };
  const getConfigType = (configuration, selectedDeviceConfiguration) => {
    if (configuration === selectedDeviceConfiguration) {
      return 'device-specific';
    }
    return 'state-specific';
  };
  const filterLocations = (locations, selected, options) => {
    const formattedSku = sku.replaceAll(/[-\\.]/ig, '_');
    const selectedOption = options.find(opt => opt.id === selected);
    const configType = getConfigType(selected, selectedDeviceConfiguration);
    const desiredField = configType === 'device-specific' ? 'desiredDeviceSpecific' : 'desiredStateSpecific';
    const validLocations = locations.filter(location => location?.data?.config?.[formattedSku]?.[desiredField]?.keystreamId !== selectedOption?.keystreamId);
    const validLocationIds = validLocations.map(location => location.id);
    const omittedLocations = locations.filter(location => !validLocationIds.includes(location.id));
    return {
      validLocations,
      omittedLocations
    };
  };
  const getConfigurationsToBeUpdated = () => {
    const retObject = {};
    const isDeviceConfigValid = isConfigValid(selectedDeviceConfiguration);
    const {
      validLocations: validDeviceLocations,
      omittedLocations: omittedDeviceLocations
    } = filterLocations(locations, selectedDeviceConfiguration, deviceConfigurationOptions);
    if (isDeviceConfigValid) {
      retObject.deviceSpecific = {
        sku,
        validLocations: validDeviceLocations,
        omittedLocations: omittedDeviceLocations,
        selectedConfiguration: selectedDeviceConfiguration
      };
    }
    const isStateConfigValid = isConfigValid(selectedStateConfiguration);
    const {
      validLocations: validStateLocations,
      omittedLocations: omittedStateLocations
    } = filterLocations(locations, selectedStateConfiguration, stateConfigurationOptions);
    if (isStateConfigValid) {
      retObject.stateSpecific = {
        sku,
        validLocations: validStateLocations,
        omittedLocations: omittedStateLocations,
        selectedConfiguration: selectedStateConfiguration
      };
    }
    return retObject;
  };
  const sendNewConfigurations = useCallback(async () => {
    const systemId = Molten.getConfig('api/systemId');
    const configurationsToBeUpdated = getConfigurationsToBeUpdated();
    setLoading(true);
    try {
      const configurationsWithReponse = await Object.entries(configurationsToBeUpdated).reduce(async (pAcc, [key, {
        sku,
        validLocations,
        selectedConfiguration
      }]) => {
        const acc = await pAcc;
        if (validLocations.length > 0) {
          if (server === 'kudelskidevice') {
            const response = await GlobalState.dispatch(async (dispatch, getState, {
              api
            }) => api.system(systemId).srv('kudelskidevice').post('setDesiredConfigByImagineGroups', {
              deviceSku: sku,
              locations: validLocations,
              configId: selectedConfiguration
            }));
            acc[key].response = response;
          } else {
            const response = await GlobalState.dispatch(async (dispatch, getState, {
              api
            }) => api.system(systemId).srv('recovr').post('setDesiredConfigByLocations', {
              locatorSku: sku,
              locations: validLocations,
              configId: selectedConfiguration
            }));
            acc[key].response = response;
          }
        }
        return acc;
      }, Promise.resolve(configurationsToBeUpdated));
      showResponseToasts(configurationsWithReponse);
    } catch (err) {
      Toast.error(['Failed to update configuration', err?.message]);
    }
    onClose();
    setLoading(false);
  }, [selectedDeviceConfiguration, selectedStateConfiguration]);
  return {
    loading,
    sendNewConfigurations
  };
};
export default useUpdateDeviceAndStateConfigs;